import React from "react";
import { Link } from "react-router-dom";
import { LogOut } from "lucide-react";
import CompanyAvatar from "./CompanyAvatar";
import { UnreadBadge } from "./NotificationsMenu";

// Account header and items shared by the desktop dropdown and the mobile drawer,
// so both menus always look and behave the same.

export function AccountMenuHeader({ user }) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <CompanyAvatar src={user?.avatar_url} alt={user?.name || ""} />
      {/* Aligned to the interface's start side, next to the logo; <bdi> lets an Arabic or
          English name still render in its own direction. */}
      <div className="min-w-0 flex-1 text-start">
        <p
          className="line-clamp-2 break-words text-[13px] font-semibold leading-snug text-slate-900 dark:text-white"
          title={user?.name || "User"}
        >
          <bdi>{user?.name || "User"}</bdi>
        </p>
        {user?.email && (
          <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400" title={user.email}>
            <bdi dir="ltr">{user.email}</bdi>
          </p>
        )}
      </div>
    </div>
  );
}

export function AccountMenuDivider() {
  return <div className="my-1 h-px bg-slate-100 dark:bg-slate-700" />;
}

// "compact" rows for the desktop dropdown, taller "touch" rows for the mobile drawer.
const ROW_SIZES = {
  compact: "py-2",
  touch: "min-h-11 py-2.5",
};

export function AccountMenuItems({ items, pathname, onNavigate, onLogout, logoutLabel, size = "compact", itemRole }) {
  const row = `group flex w-full items-center gap-3 rounded-lg px-2.5 text-sm transition-colors duration-150 ${ROW_SIZES[size] ?? ROW_SIZES.compact}`;

  return (
    <>
      {items.map((item) => {
        const Icon = item.icon;
        const isActive = !item.download && pathname === item.path;
        const className = `${row} ${
          isActive
            ? "bg-emerald-50 font-semibold text-(--primary-green) dark:bg-emerald-500/10 dark:text-emerald-400"
            : "font-medium text-slate-700 hover:bg-slate-100 hover:text-slate-900 active:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700 dark:hover:text-white dark:active:bg-slate-700"
        }`;
        const content = (
          <>
            {Icon && (
              <Icon
                size={16}
                strokeWidth={2.25}
                aria-hidden="true"
                className={`shrink-0 transition-colors ${isActive ? "" : "text-slate-400 group-hover:text-(--primary-green) dark:text-slate-500 dark:group-hover:text-emerald-400"}`}
              />
            )}
            {item.label}
            <UnreadBadge count={item.badge} className="ms-auto" />
          </>
        );

        return item.download ? (
          <a key={item.label} role={itemRole} href={item.path} download onClick={onNavigate} className={className}>
            {content}
          </a>
        ) : (
          <Link key={item.path} role={itemRole} to={item.path} onClick={onNavigate} className={className}>
            {content}
          </Link>
        );
      })}
      <AccountMenuDivider />
      <button
        type="button"
        role={itemRole}
        onClick={onLogout}
        className={`${row} font-medium text-red-600 hover:bg-red-50 active:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 dark:active:bg-red-500/10`}
      >
        <LogOut size={16} strokeWidth={2.25} aria-hidden="true" className="shrink-0 rtl:-scale-x-100" />
        {logoutLabel}
      </button>
    </>
  );
}
